import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import { WORK_FROM_HOME } from 'components/zpFilter/filterUtils';
import { useTracking } from 'hooks';
import { jobService } from 'services';
import { dispatchAddJobToApplied, dispatchJobListDismiss, dispatchJobSeen, getPaidJobs } from 'store/jobs/actions';
import { dispatchOpenDLPModal } from 'store/modals/actions';
import {
  buildCmpSubpagesUrls,
  buildDlpUrl,
  generateKey,
  getStateName,
  isUserOutsideUS,
  openInNewTab,
} from 'utils';
import {
  CLUSTER_HEADER_CATEGORY,
  CMP_SUBPAGES,
  CPP_SUBPAGES,
  DLP_VIEW_SOURCE,
  PAID_JOBS_CATEGORY,
  TRACKING,
  USER_ACTIVITY_EVENTS,
} from 'utils/constant';

import classes from './index.module.css';

const propTypes = {
  title: PropTypes.string.isRequired,
  origin: PropTypes.string.isRequired,
  initialPayload: PropTypes.objectOf(PropTypes.any).isRequired,
  jobsPageIndexable: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  companyLink: PropTypes.string,
  usingPaidAPI: PropTypes.bool,
  dependancyInstace: PropTypes.object,
  stretchVertically: PropTypes.bool,
  onSide: PropTypes.bool,
  standardSideBarWidth: PropTypes.bool,
  isRemote: PropTypes.bool,
  onEmpty: PropTypes.func,
  src: PropTypes.string,
};

const defaultProps = {
  title: '',
  origin: '',
  initialPayload: {},
  jobsPageIndexable: '',
  companyLink: '',
  usingPaidAPI: false,
  dependancyInstace: { shallIGo: true, previousListingHashes: [] },
  stretchVertically: false,
  onSide: false,
  standardSideBarWidth: false,
  isRemote: false,
  onEmpty: () => { },
  src: '',
};

const MAX_JOBS_AMOUNT_IN_LIST = 6;

const JobList = ({
  title,
  initialPayload,
  jobsPageIndexable,
  origin,
  companyLink,
  usingPaidAPI,
  stretchVertically,
  onSide,
  standardSideBarWidth,
  isRemote,
  onEmpty,
  src
}) => {
  const [loading, setLoading] = useState(true);
  const [listOfJobs, setListOfJobs] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const dispatch = useDispatch();
  const locals = useSelector(({ locals }) => locals);
  const PAID_JOB_KEY = useMemo(() => 'JOB_LIST_BOX' + uuidv4().slice(0, 10), []);
  const paidJobsBucket = useSelector(({ jobs }) => jobs[PAID_JOB_KEY] || {});
  const criteria = useSelector((state) => state.locals.criteria);

  const tracking = useTracking({ jobGroup: PAID_JOB_KEY });

  const hidePaidJobsBucket = paidJobsBucket
    ? paidJobsBucket.hideOnEmpty &&
      paidJobsBucket.totalJobs === 0 &&
      paidJobsBucket.loading === false &&
      !paidJobsBucket.error
    : false;

  useEffect(() => {
    if (paidJobsBucket?.jobs) {
      setListOfJobs(paidJobsBucket.jobs);
    }
  }, [paidJobsBucket.jobs]);

  useEffect(() => {
    setLoading(paidJobsBucket.loading);
  }, [paidJobsBucket.loading]);

  const getJobLocation = (job) =>
    job?.OBJjobTags?.indexOf('Remote') !== -1 && job?.location
      ? `Remote or ${job?.location}`
      : job?.OBJjobTags?.indexOf('Remote') !== -1
      ? 'Remote'
      : job?.location || '';

  const fetchJobs = async () => {
    try {
      let jobServiceAPI;
      let payload = {};
      if (isRemote && !isUserOutsideUS(criteria)) {
        payload.remoteCategories = [WORK_FROM_HOME];
      }
      if (initialPayload?.numJobs) {
        payload.numJobs = initialPayload.numJobs;
      }
      const validLocations = (
        initialPayload?.locations?.filter(
          (location) => location.city || location.state || location.stateName
        ) || []
      ).map(({ city, state }) => ({ city, state }));
      const isValidTitle = !!initialPayload?.title;
      const isValidCompanyIDs = !!initialPayload?.companyIDs?.length;
      initialPayload.paidJobAPICategory =
        initialPayload?.paidJobAPICategory ?? PAID_JOBS_CATEGORY.CAREER_CLUSTER; // When you don't provide paidJobAPICategory then it will consider 'careerCluster' by default
      const shouldFetchPaidJobs =
        usingPaidAPI &&
        !locals.displayPaidJobs.hideOnRightJobList &&
        (initialPayload.paidJobAPICategory === PAID_JOBS_CATEGORY.COMPANY_CLUSTER
          ? isValidCompanyIDs
          : true) &&
        ([PAID_JOBS_CATEGORY.CAREER_CLUSTER].includes(initialPayload.paidJobAPICategory)
          ? validLocations.length
          : true) &&
        ([PAID_JOBS_CATEGORY.CAREER_CLUSTER, PAID_JOBS_CATEGORY.LOCATION].includes(
          initialPayload.paidJobAPICategory
        )
          ? isValidTitle
          : true);
      if (shouldFetchPaidJobs) {
        jobServiceAPI = jobService.getPaidJobs;
        payload = {
          ...payload,
          companyIDs: initialPayload?.companyIDs ?? undefined,
          locations: validLocations,
          titles: isValidTitle
            ? [
                {
                  originalSearch: initialPayload?.title,
                  normalizedTitle: initialPayload?.title,
                },
              ]
            : undefined,
          category: initialPayload?.paidJobAPICategory,
          companyCategories: initialPayload?.companyCategories,
        };
        return dispatch(
          getPaidJobs({
            key: PAID_JOB_KEY,
            detail: payload,
          })
        );
      } else {
        setLoading(true);
        switch (origin) {
          case CMP_SUBPAGES.SKILLS: {
            jobServiceAPI = jobService.getJobsBySkill;

            const { locations, title, skill } = initialPayload;
            const city = locations && locations[0] ? locations[0].city : '';
            const state = locations && locations[0] ? getStateName(locations[0].state) : '';
            payload = {
              ...payload,
              locationSort: true,
              previousListingHashes: [],
              remoteCategories: [WORK_FROM_HOME],
              skill: skill,
              city: city,
              state: state,
              title: title,
            };
            break;
          }
          case CPP_SUBPAGES.HISTORY:
          case CLUSTER_HEADER_CATEGORY.COMPANY_SALARY:
          case CPP_SUBPAGES.DEMOGRAPHICS: {
            jobServiceAPI = jobService.getCompanyJobs;
            const {
              jobTitle,
              companyID,
              remoteCategories,
              locations: companyLocations,
            } = initialPayload;
            payload = {
              ...payload,
              companyID,
              jobTitle: jobTitle,
              locationSort: true,
              previousListingHashes: [],
              locations: companyLocations,
              remoteCategories,
            };
            break;
          }
          case CPP_SUBPAGES.OVERVIEW: {
            const { locations: nearbyLocations, paidJobAPICategory } = initialPayload;
            const mappedLocations = nearbyLocations?.length
              ? nearbyLocations.map((l) => ({
                  city: l.city,
                  stateName: paidJobAPICategory ? getStateName(l.state) : l.state,
                }))
              : [];

            payload = {
              ...payload,
              ...initialPayload,
              locations: mappedLocations,
            };
            jobServiceAPI = jobService.getTitleJobs;
            break;
          }
          default:
            jobServiceAPI = jobService.getTitleJobs;
            payload = {
              ...payload,
              ...initialPayload,
            };
        }
      }
      let jobs = [];
      try {
        const response = await jobServiceAPI(payload);
        jobs = response.jobs ? response.jobs.slice(0, MAX_JOBS_AMOUNT_IN_LIST) : [];
        setListOfJobs(jobs);
        if (jobs?.length === 0) {
          dispatch(dispatchJobListDismiss({ jobListDismiss: true }));
          setIsEmpty(true);
        } else {
          setIsEmpty(false);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      setIsEmpty(true);
      return;
    }
  };

  const openDLPModal = (job) => {
    if (job.jobDLPUrl) {
      dispatch(dispatchOpenDLPModal(job));
    }
  };

  const onJobListItemClicked = (event, job) => {
    const { metaKey, ctrlKey } = event;
    const openModal = !metaKey && !ctrlKey;
    const disableExternalClick = !!(
      criteria.userOutsideUS &&
      job.sponsorFlag &&
      !job.easyApplyFlag
    );
    // log the activity
    if (job.cpc > 0 && !disableExternalClick) {
      dispatch(dispatchAddJobToApplied({ job, logActivity: false }));
      tracking.openJobExternal(job, {
        activityCategory: TRACKING.LOG_ACTIVITY.DLP,
        activityData: {
          activityType: USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_JOB_CLICKED,
          payload: {
            url: window.location.href + src,
          },
          pageCategory: window.pageCategory,
        },
      });
    } else {
      const jobWithDLP = {
        ...job,
        jobDLPUrl: buildDlpUrl(job),
      };
      if (openModal || disableExternalClick) {
        dispatch(
          dispatchJobSeen({
            job,
            viewSource: DLP_VIEW_SOURCE.AFTER_UNPAID_CLICK,
          })
        );
        tracking.logActivity(
          {
            job,
            type: USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_VIEWED_DLP,
            url: window.location.href + src,
            pageCategory: window.pageCategory,
            viewSource: DLP_VIEW_SOURCE.AFTER_UNPAID_CLICK,
          },
          TRACKING.LOG_ACTIVITY.DLP
        );
        openDLPModal(jobWithDLP);
      } else {
        openInNewTab(jobWithDLP.jobDLPUrl);
      }
    }
  };

  const renderLoadingBlock = () => {
    const loadingBlocks = Array(8).fill(
      ...[
        {
          titleWidth: '70%',
          subtitleWidth: '50%',
        },
        {
          titleWidth: '60%',
          subtitleWidth: '80%',
        },
      ]
    );
    return loadingBlocks.map(({ titleWidth, subtitleWidth }, idx) => (
      <Fragment key={generateKey(idx)}>
        <div className={classes['loading-state-block-title']} style={{ width: titleWidth }} />
        <div className={classes['loading-state-block-subtitle']} style={{ width: subtitleWidth }} />
        {idx !== loadingBlocks.length - 1 && (
          <div className={classes['loading-state-block-separator']} />
        )}
      </Fragment>
    ));
  };

  const getJobsPaGeLink = () => {
    switch (origin) {
      case CLUSTER_HEADER_CATEGORY.COMPANY_SALARY:
        return companyLink;
      case CPP_SUBPAGES.HISTORY:
        return `${companyLink}${CMP_SUBPAGES.JOBS}`;
      case CMP_SUBPAGES.SALARY:
        return (
          buildCmpSubpagesUrls(initialPayload.title, '', CMP_SUBPAGES.JOBS) +
          '#src=' +
          window.pageCategory +
          '&callout_location=high-paying-jobs'
        );
      default:
        return buildCmpSubpagesUrls(initialPayload.title, '', CMP_SUBPAGES.JOBS);
    }
  };

  const renderJobsList = () => {
    const jobsPageLink = getJobsPaGeLink();

    return (
      <Fragment>
        <ul className={classes['job-list']}>
          {listOfJobs.map((job) => (
            <li key={job.listingHash} className={classes['job-list-item']}>
              <div onClick={(e) => onJobListItemClicked(e, job)} className="list-link">
                {job.jobTitle}
              </div>
              <div className={classes['job-list-item-data']}>
                <label className="company-name">{job.companyName}</label>
                <label className="dot-separator" />
                <label className="location">{getJobLocation(job)}</label>
              </div>
            </li>
          ))}
        </ul>
        {jobsPageIndexable && <a href={jobsPageLink} className="list-link">{`View All Jobs`}</a>}
      </Fragment>
    );
  };

  if ((!loading && listOfJobs.length === 0 && hidePaidJobsBucket) || isEmpty) {
    onEmpty();
    return null;
  }

  return (
    <InView
      as="div"
      className={classnames(classes['job-list-container'], {
        [classes.stretchVert]: stretchVertically,
        [classes.side]: onSide,
        [classes.standardSideBarWidth]: standardSideBarWidth,
      })}
      onChange={(entered) => {
        if (entered) {
          fetchJobs();
        }
      }}
      triggerOnce
    >
      <h3 className={classes['subtitle']}>{title}</h3>

      {!!(loading || paidJobsBucket.loading) && renderLoadingBlock()}

      {!loading && listOfJobs.length > 0 && renderJobsList()}
    </InView>
  );
};

JobList.propTypes = propTypes;
JobList.defaultProps = defaultProps;

export default JobList;
